/**
 * Minimal OpenAI-compatible chat-completions client.
 *
 * Works against any server that implements POST {baseUrl}/chat/completions
 * with the OpenAI request/response shape (OpenAI itself, Ollama, LM Studio,
 * vLLM, OpenRouter, local FastChat, etc.).
 *
 * Browsers enforce CORS on cross-origin requests, and most OpenAI-compatible
 * servers don't send Access-Control-Allow-Origin — so a direct fetch from the
 * taskpane to a third-party endpoint is blocked. When `useProxy` is on we
 * POST same-origin to `/api/llm/chat/completions` and pass the real base URL
 * in the `x-bishu-target-base` header; the proxy (the Vite dev server in dev,
 * a Cloudflare Pages Function in production) forwards the request server-side
 * (where CORS doesn't apply) and adds permissive CORS on the way back. See
 * vite.config.ts → bishuLlmProxy and functions/api/llm.
 */
import type {
  LlmConfig,
  LlmCompletionRequest,
  LlmCompletionResponse,
  LlmMessage,
  AppError,
} from "@/types";

export class LlmClientError extends Error implements AppError {
  isNetwork?: boolean;
  constructor(message: string, isNetwork = false) {
    super(message);
    this.name = "LlmClientError";
    this.isNetwork = isNetwork;
  }
}

export class LlmClient {
  constructor(private cfg: LlmConfig) {}

  /** The URL we actually fetch. Same-origin proxy path when the proxy is on. */
  private get endpoint(): string {
    if (this.cfg.useProxy) return "/api/llm/chat/completions";
    const base = this.cfg.baseUrl.replace(/\/+$/, "");
    return `${base}/chat/completions`;
  }

  async complete(messages: LlmMessage[]): Promise<string> {
    const body: LlmCompletionRequest = {
      model: this.cfg.model,
      messages,
      temperature: this.cfg.temperature,
      max_tokens: this.cfg.maxTokens > 0 ? this.cfg.maxTokens : undefined,
      stream: false,
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.cfg.apiKey) {
      headers["Authorization"] = `Bearer ${this.cfg.apiKey}`;
    }
    if (this.cfg.useProxy) {
      // Tell the proxy where to forward. The real base URL, trailing slash trimmed.
      headers["x-bishu-target-base"] = this.cfg.baseUrl.replace(/\/+$/, "");
    }

    let res: Response;
    try {
      res = await fetch(this.endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
    } catch (e) {
      throw new LlmClientError(
        `Could not reach LLM server at ${this.endpoint}. ${String(e)}`,
        true,
      );
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new LlmClientError(
        `LLM server returned ${res.status} ${res.statusText}${text ? `: ${text.slice(0, 500)}` : ""}`,
        true,
      );
    }

    let data: LlmCompletionResponse;
    try {
      data = (await res.json()) as LlmCompletionResponse;
    } catch (e) {
      throw new LlmClientError(`LLM server returned non-JSON response: ${String(e)}`);
    }

    const choice = data.choices?.[0];
    if (!choice?.message?.content) {
      throw new LlmClientError("LLM server returned no completion text.");
    }
    return choice.message.content.trim();
  }
}
